import React, { useImperativeHandle, useRef, forwardRef } from "react";
import styled from "styled-components";

import { useCallback, useEffect, useState } from "react";

const Container = styled.div`
  position: relative;

  & > p {
    position: absolute;
    color: var(--color-aux-danger-700);
    font-size: 10px;
    font-family: var(--font-paragraph-default);
  }
`;

const Subcontainer = styled.button<{
  $onError: "hasError" | "noError";
  $inputString: string;
  $disabled: "disabled" | "enabled";
}>`
  position: relative;
  width: 280px;

  height: 47px;
  padding: 14px 12px;

  border-radius: 8px;
  border: 1px solid
    ${({ $onError }) =>
      $onError === "hasError"
        ? `var(--color-aux-danger-700)`
        : `var(--color-dark-blue-300)`};

  background: ${({ $disabled }) =>
    $disabled === "disabled"
      ? `var(--color-light-100)`
      : `var(--color-light-0)`};

  cursor: ${({ $disabled }) => ($disabled === "enabled" ? "text" : "default")};

  ${({ $disabled }) =>
    $disabled === "disabled" &&
    `& > input {
    background: var(--color-light-100);
    color: var(--color-text-labels);
  }`}

  /* Label and mandatory styles removed */

  ${({ $disabled }) =>
    $disabled !== "disabled" &&
    `&:hover {
    background: var(--color-light-0);
  }`}

  .clear {
    position: absolute;
    right: 12px;

    top: 12px;

    color: var(--color-text-labels);
    width: 18px;
    height: 18px;
    font-size: 14px;

    cursor: pointer;
    outline: none;
  }

  &:focus-within {
    border: 1px solid var(--color-main-blue);
  }

  .clear:focus {
    border: 1px solid var(--color-main-blue);
  }
`;

const Input = styled.input<{ $disabled: "enabled" | "disabled" }>`
  width: 100%;
  height: 100%;
  border: 0;
  color: var(--color-text-paragraph);
  padding: 0;
  display: flex;
  align-items: center;
  ${({ $disabled }) => $disabled === "disabled" && `cursor: default;`}

  outline: none;

  font-size: 14px;

  &::placeholder {
    font-size: 14px;
    color: var(--color-text-labels);
    vertical-align: middle;
    line-height: 1.2;
    opacity: 1;
    transition: opacity 0.15s;
  }

  &:focus::placeholder {
    opacity: 0;
    transition: opacity 0.15s;
  }
`;

type TTextInputProps = {
  id?: string;
  className?: string;
  placeholder?: string;
  value?: string;
  onError?: { hasError: boolean; message: string };
  onChange?: (inputValue: string) => void;
  filled?: string;
  disabled?: boolean;
  onBlur?: (inputValue: string) => void;
  setData?: (
    setInputString: React.Dispatch<React.SetStateAction<string>>
  ) => void;
  customFilter?: (
    inputRef: HTMLInputElement,
    setInputString: React.Dispatch<React.SetStateAction<string>>
  ) => void;
};

const TextInput = forwardRef<HTMLInputElement, TTextInputProps>(
  (
    {
      placeholder,
      value,
      id,
      className,
      setData,
      onError,
      onChange,
      filled,
      disabled,
      onBlur,
      customFilter,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose the inputRef to parent via forwarded ref
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [inputString, setInputString] = useState("");
    const [, /* shouldShowClearButton */ setShoulShowClearButton] =
      useState(false);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        if (!customFilter) {
          setInputString(e.target.value);
        } else {
          if (inputRef.current !== null)
            customFilter(inputRef.current, setInputString);
        }
        if (onChange) onChange(e.target.value);
      }
    };

    const handleFocus = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      e.preventDefault();
      setShoulShowClearButton(true);
      inputRef.current?.focus();
    };

    const handleOnblur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      e.preventDefault();
      setShoulShowClearButton(false);
      if (inputRef.current) {
        if (onBlur) onBlur(inputRef.current.value);
        inputRef.current.blur();
      }
    };

    const handleFilled = useCallback(() => {
      if (filled && inputRef.current) {
        setInputString(filled);
        inputRef.current.value = filled;
      }
    }, [filled]);

    useEffect(() => {
      if (setData) {
        setData(setInputString);
      }
    }, []);

    useEffect(() => {
      if (inputRef.current) inputRef.current.value = inputString;
    }, [inputString]);

    useEffect(() => {
      handleFilled();
    }, [handleFilled]);

    return (
      <Container className={className}>
        <Subcontainer
          onBlur={handleOnblur}
          $disabled={disabled ? "disabled" : "enabled"}
          $onError={(onError?.hasError && "hasError") || "noError"}
          id={id && id}
          role="button"
          onClick={handleFocus}
          $inputString={inputString}
          disabled={disabled}
        >
          <Input
            $disabled={disabled ? "disabled" : "enabled"}
            disabled={disabled}
            onChange={handleOnChange}
            ref={inputRef}
            type="text"
            value={value}
            placeholder={placeholder}
          />
          {/* {shouldShowClearButton && inputString.length > 0 && !filled && (
          <UilTimes
            onClick={handleOnClick}
            tabIndex={0}
            role="button"
            className="clear"
          />
        )} */}
        </Subcontainer>
        {onError && onError.hasError && <p>{onError.message}</p>}
      </Container>
    );
  }
);

export default TextInput;
